<script>
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Spinner from 'shared/components/Spinner.vue';

const MAX_ATTACHMENTS = 5;

export default {
  name: 'CannedResponseAttachments',
  components: { Spinner },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  data() {
    return { uploading: false };
  },
  methods: {
    async onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      // Reset so re-selecting the same file still fires @change.
      event.target.value = '';
      if (!files.length) return;

      const room = MAX_ATTACHMENTS - this.modelValue.length;
      if (room <= 0) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENTS.MAX_COUNT_ERROR'));
        return;
      }

      this.uploading = true;
      try {
        const added = await Promise.all(
          files.slice(0, room).map(file =>
            uploadFile(file).then(({ blobId, fileUrl }) => ({
              blobSignedId: blobId,
              url: fileUrl,
              filename: file.name,
              contentType: file.type,
              byteSize: file.size,
            }))
          )
        );
        this.$emit('update:modelValue', [...this.modelValue, ...added]);
      } catch (error) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR'));
      } finally {
        this.uploading = false;
      }
    },
    removeAttachment(index) {
      const next = [...this.modelValue];
      next.splice(index, 1);
      this.$emit('update:modelValue', next);
    },
  },
};
</script>

<template>
  <div class="w-full">
    <div v-if="modelValue.length" class="flex flex-col gap-1 mb-2">
      <div
        v-for="(file, index) in modelValue"
        :key="file.blobSignedId || file.blobId || index"
        class="flex items-center justify-between gap-2 rounded-md border border-n-weak px-2 py-1 text-sm"
      >
        <a
          :href="file.url"
          target="_blank"
          rel="noopener noreferrer"
          class="truncate text-n-slate-12"
        >
          {{ file.filename }}
        </a>
        <button
          type="button"
          class="flex-shrink-0 text-n-slate-11 hover:text-n-ruby-9"
          @click="removeAttachment(index)"
        >
          <fluent-icon icon="dismiss-circle" class="size-4" />
        </button>
      </div>
    </div>

    <label
      class="flex items-center w-fit gap-2 h-8 px-2 text-sm rounded-lg border border-dashed cursor-pointer border-n-strong"
    >
      <input
        type="file"
        multiple
        accept="image/*,application/pdf"
        class="hidden"
        :disabled="uploading"
        @change="onFilesSelected"
      />
      <Spinner v-if="uploading" />
      <fluent-icon v-else icon="attach" class="size-4" />
      <span>
        {{
          uploading
            ? $t('CANNED_MGMT.ATTACHMENTS.UPLOADING')
            : $t('CANNED_MGMT.ATTACHMENTS.ADD_BUTTON')
        }}
      </span>
    </label>
    <p class="mt-1 text-xs text-n-slate-11">
      {{ $t('CANNED_MGMT.ATTACHMENTS.HINT') }}
    </p>
  </div>
</template>
